<script>
    import { navigate } from "svelte-routing";
    import { onMount } from "svelte";
    import { openModal } from "svelte-modals";
    import FormCard from "../FormCard.svelte";
    import GroupSearcher from "../../SearchBars/GroupSearcher.svelte";
    import SelectMunicipalitiesTable from "./SelectMunicipalitiesTable.svelte";
    import Button from "../../Button/Button.svelte";
    import SelectGroupTreeModal from "../../Modal/SelectGroupTreeModal.svelte";
    import SelectFederationsModal from "../../Modal/SelectFederationsModal.svelte";
    import toast from "svelte-french-toast";
    import { settings } from "../../../stores/settingsStore";
    import { createJurisdiction, updateJurisdiction, getJurisdictionByGroupAndYear } from "../../../services/jurisdictionService";

    const urlParams = new URLSearchParams(window.location.search);
    const editGroupId = urlParams.get("groupId");
    const editYear = urlParams.get("year");
    const isEdit = !!editGroupId && !!editYear;

    let groupId = isEdit ? editGroupId : undefined;
    let groupName = "";
    let municipalities = []; // array of { label, value: cityId }
    let initialMunicipalityIds = []; // ids loaded in edit, to compute the diff
    let isPressed = false;
    let selectedCircles = []; // [{ id, name }] picked from the group tree (display only for now)

    function openParentModal() {
        openModal(SelectGroupTreeModal, {
            groupId: groupId,
            groupName: groupName,
            selectedIds: selectedCircles.map((c) => c.id),
            onConfirm: () => {
                // circles were moved under this federation; reset the selection
                selectedCircles = [];
            }
        });
    }

    function openFederationsModal() {
        openModal(SelectFederationsModal, {
            groupId: groupId
        });
    }

    $: municipalityIds = municipalities?.map((m) => m.value) ?? [];
    $: year = isEdit ? +editYear : $settings.subscriptionActiveYear;

    // diff to connect/disconnect when editing
    $: municipalitiesDiff = [
        ...municipalityIds
            .filter((id) => !initialMunicipalityIds.includes(id))
            .map((id) => ({ id, toBeDisconnected: false })),
        ...initialMunicipalityIds
            .filter((id) => !municipalityIds.includes(id))
            .map((id) => ({ id, toBeDisconnected: true }))
    ];

    $: isButtonDisabled = isPressed || !groupId || (isEdit ? municipalitiesDiff.length === 0 : municipalityIds.length === 0);

    function loadJurisdiction() {
        return getJurisdictionByGroupAndYear(editGroupId, editYear).then((resp) => {
            const data = resp.data;

            groupId = data.groupId ?? data.group?.id ?? editGroupId;
            groupName = data.groupName ?? data.group?.name ?? "";
            municipalities = (data.municipalities ?? []).map((m) => ({ label: m.name, value: m.id }));
            initialMunicipalityIds = municipalities.map((m) => m.value);
        }).catch((err) => {
            if (err?.response?.data?.status === 404 || err?.response?.data?.status === 500) {
                toast.error("Sottogruppo non trovato");
                navigate("table", { replace: true });
                return;
            }

            toast.error(`Errore durante il caricamento, errore ${err.toString()}`);
        });
    }

    onMount(async () => {
        if (!isEdit) {
            return;
        }

        loadJurisdiction();
    });

    function handleSubmit() {
        isPressed = true;

        const request = isEdit
            ? updateJurisdiction({ groupId, year, municipalities: municipalitiesDiff })
            : createJurisdiction({ groupId, municipalityIds });

        request.then((resp) => {
            isPressed = false;

            if (resp.status === 200 && resp?.data) {
                toast.success(isEdit ? "Sottogruppo modificato" : "Sottogruppo creato");

                if (isEdit) {
                    loadJurisdiction();
                    return;
                }

                navigate("table", { replace: true });
            }
        }).catch((err) => {
            isPressed = false;

            if (err?.response?.status === 500) {
                toast.error(`Impossibile ${isEdit ? "modificare" : "creare"} il sottogruppo, errore ${err.toString()}`);
            }
        });
    }
</script>

<FormCard
    subject="il sottogruppo per l'anno {year}"
    isEdit={isEdit}
    isButtonDisabled={isButtonDisabled}
    hasDeleteButton={false}
    handleSubmit={handleSubmit}
>
    <div class="w-full">
        <div class="font-450 text-lg">Federazione</div>

        {#if isEdit}
            <div class="flex items-center h-11 input-box-disabled pl-4">
                {groupName}
            </div>
        {:else}
            <GroupSearcher
                isUserBased={true}
                groupType="FEDERATION"
                required={false}
                on:change={(e) => groupId = e.detail.value}
                on:clear={() => groupId = undefined}
            />
        {/if}
    </div>

    <div class="w-full">
        <div class="font-450 text-lg">Comuni</div>

        <SelectMunicipalitiesTable groupId={groupId} year={year} bind:value={municipalities} />
    </div>

    <div slot="header-content" class="flex flex-col gap-2 md:flex-row">
        {#if isEdit}
            <Button
                handleClick={openParentModal}
                buttonClass="w-full md:w-48 bg-lightBlue-500 text-white"
                content="CAMBIA CIRCOLI"
            />

            <Button
                handleClick={openFederationsModal}
                buttonClass="w-full md:w-64 bg-lightBlue-500 text-white"
                content="AGGIORNA TESSERAMENTI"
            />
        {/if}
    </div>
</FormCard>
